import { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Package, Users, Ticket, FileCheck2, Megaphone, Wallet, Repeat, BarChart3, History, Settings, LogOut, Menu, X, Loader2, Server, Sun, Moon, ShieldCheck, LifeBuoy } from "lucide-react";
import { Logo } from "@/components/customer/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pedidos", label: "Pedidos", icon: ShoppingBag },
  { to: "/admin/comprobantes", label: "Comprobantes", icon: FileCheck2 },
  { to: "/admin/tickets", label: "Tickets", icon: LifeBuoy },
  { to: "/admin/servicios", label: "Servicios", icon: Package, owner: true },
  { to: "/admin/proveedores", label: "Proveedores", icon: Server, owner: true },
  { to: "/admin/saldo-proveedores", label: "Saldo proveedores", icon: Wallet },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/cupones", label: "Cupones", icon: Ticket, owner: true },
  { to: "/admin/suscripciones", label: "Recurrencia", icon: Repeat },
  { to: "/admin/trafico", label: "Tráfico pago", icon: Megaphone },
  { to: "/admin/costos", label: "Costos", icon: Wallet },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
  { to: "/admin/actividad", label: "Actividad", icon: History },
  { to: "/admin/configuracion", label: "Configuración", icon: Settings, owner: true },
  { to: "/admin/administradores", label: "Administradores", icon: ShieldCheck, owner: true },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5 border-b border-slate-800"><Logo dark size="sm" /></div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.filter((n) => !n.owner || user?.role === "owner").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`adminnav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
          >
            <n.icon className="h-4.5 w-4.5 h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800 space-y-1">
        <button onClick={toggle} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 w-full" data-testid="admin-theme-toggle">
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />} {theme === "dark" ? "Modo claro" : "Modo oscuro"}
        </button>
        <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 w-full" data-testid="admin-logout">
          <LogOut className="h-5 w-5" /> Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-slate-900 fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-slate-900"><SidebarContent /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        {/* topbar mobile */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 h-14 flex items-center justify-between px-4">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="h-6 w-6 text-slate-700" /></button>
          <Logo size="sm" />
          <button onClick={toggle} data-testid="admin-theme-toggle-mobile" className="text-slate-600">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
        </div>
        <main className="p-5 sm:p-7 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
